import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { STREAK_EVENT_GOAL } from 'src/replicant/streakbattleroyale/streakbattleroyale.ruleset';
import { now, user } from 'src/StateObserver';
import { getDailyChallengeId } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.getters';
import { getActiveRace } from 'src/replicant/match2/races/races.getters';
import { getMatch2FrenzyEventConfig } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import {
  getDailyChallengeObjectiveIcon,
  getFrenzyObjectiveIcon,
} from '../../match2.icons';

const assets = {
  race: {
    sugar: 'assets/ui/match2/races/sugar/icon.png',
    crown: 'assets/ui/match2/races/crown/icon.png',
    turtle: 'assets/ui/match2/common/icon_pinch_achievement_race.png',
  },
  fujiQuest: 'assets/ui/match2/common/icon_pinch_achievement_fuji_quest.png',
  treasureExpress:
    'assets/ui/match2/common/icon_pinch_achievement_treasure_express.png',
  league: 'assets/ui/match2/common/icon_pinch_achievement_league.png',
};

export const match2AchievementTypes = [
  'frenzy',
  'dailyChallenge',
  'race',
  'fujiQuest',
  'treasureExpress',
  'league',
] as const;
type Match2AchievementType = (typeof match2AchievementTypes)[number];

type Match2PinchStreakBarOpts = ViewOpts & {
  type: Match2AchievementType;
  count: number;
};

export default class Match2AchievementItem extends View {
  constructor(opts: Match2PinchStreakBarOpts) {
    super({
      width: 90,
      height: 90,
      ...opts,
    });

    const { type, count } = opts;

    // Create icon.
    const icon = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.width,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
    });

    this.setImage(type, icon);

    // Create label.
    const labelHeight = (40 / 90) * this.style.width;
    new LangBitmapFontTextShadowView({
      superview: this,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      size: (32 / 90) * this.style.width,
      font: 'Title',
      width: this.style.width,
      height: labelHeight,
      y: this.style.height - labelHeight,
      textShadowOffsetX: 0,
      textShadowOffsetY: 5,
      textShadowOpacity: 0.35,
      localeText: () => this.getText(type, count),
    });
  }

  private setImage(type: Match2AchievementType, view: ImageScaleView) {
    if (type === 'frenzy') {
      const theme =
        getMatch2FrenzyEventConfig(user(), now())?.theme ?? 'firecracker';

      // Utilize legacy assets fetcher for frenzy icons
      getFrenzyObjectiveIcon(theme)
        .get()
        .then((image) => view.updateOpts({ image, scale: 0.9 }));
      return;
    }

    if (type === 'dailyChallenge') {
      const id = getDailyChallengeId(user()) ?? 'monday';

      // Utilize legacy assets fetcher for daily challenge icons
      getDailyChallengeObjectiveIcon(id)
        .get()
        .then((image) => view.updateOpts({ image, scale: 0.8 }));
      return;
    }

    if (type === 'race') {
      const race = getActiveRace(now());
      const theme = race?.config.theme ?? 'turtle';
      view.updateOpts({
        image: assets.race[theme],
        scale: theme === 'turtle' ? 1 : 0.8,
      });
      return;
    }

    view.setImage(assets[type]);
  }

  private getText(type: Match2AchievementType, count: number) {
    if (type === 'fujiQuest') {
      return `${count}/${STREAK_EVENT_GOAL}`;
    }
    return `+${count}`;
  }
}
